import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mail, Copy, Check, X } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import Modal from './Modal';
import { apiGet, apiPost } from '../../../utils/api';
import { toast } from 'react-hot-toast';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InviteMemberModal = ({ isOpen, onClose, projectId, onInviteSent }) => {
  const { theme } = useTheme();
  const [method, setMethod] = useState('email');
  const [recipients, setRecipients] = useState([]); // [{ id?, email, label }]
  const [role, setRole] = useState('member');
  const [inviteUrl, setInviteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  const isDark = theme === 'dark';

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const fetchSuggestions = useCallback(async (q) => {
    if (!projectId) return;
    if (q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    setSuggestLoading(true);
    try {
      const response = await apiGet(`/projects/${projectId}/invitable_users?q=${encodeURIComponent(q)}`);
      if (response.ok) {
        const data = await response.json();
        const takenIds    = new Set(recipients.map(r => r.id).filter(Boolean));
        const takenEmails = new Set(recipients.map(r => (r.email || '').toLowerCase()).filter(Boolean));
        const filtered = (data.users || []).filter(u =>
          !takenIds.has(u.id) && !(u.email && takenEmails.has(u.email.toLowerCase()))
        );
        setSuggestions(filtered);
        setHighlight(0);
      }
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
    } finally {
      setSuggestLoading(false);
    }
  }, [projectId, recipients]);

  useEffect(() => {
    if (!showDropdown) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(query), 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, showDropdown, fetchSuggestions]);

  const addRecipient = (recipient) => {
    const email = (recipient.email || '').toLowerCase().trim() || null;
    const id    = recipient.id || null;
    if (!email && !id) return;
    if (recipients.some(r =>
      (id && r.id === id) ||
      (email && r.email && r.email.toLowerCase() === email)
    )) return;
    setRecipients(prev => [...prev, { ...recipient, id, email }]);
    setQuery('');
    setSuggestions([]);
    inputRef.current?.focus();
  };

  const removeRecipient = (key) => {
    const keyStr = String(key ?? '').toLowerCase();
    setRecipients(prev => prev.filter(r =>
      String(r.id ?? '') !== String(key ?? '') &&
      (r.email || '').toLowerCase() !== keyStr
    ));
  };

  const commitRawEmail = () => {
    const raw = query.trim();
    if (!raw) return;
    if (!EMAIL_RE.test(raw)) {
      toast.error(`"${raw}" is not a valid email`);
      return;
    }
    addRecipient({ email: raw, label: raw });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown' && suggestions.length) {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, suggestions.length - 1));
      setShowDropdown(true);
    } else if (e.key === 'ArrowUp' && suggestions.length) {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && suggestions[highlight]) {
        const u = suggestions[highlight];
        addRecipient({
          id: u.id,
          email: u.email,
          label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email,
          avatar_url: u.avatar_url
        });
      } else {
        commitRawEmail();
      }
    } else if (e.key === ',' || e.key === 'Tab') {
      if (query.trim()) {
        e.preventDefault();
        commitRawEmail();
      }
    } else if (e.key === 'Backspace' && !query && recipients.length) {
      setRecipients(prev => prev.slice(0, -1));
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleSendInvitations = async () => {
    if (recipients.length === 0) {
      toast.error('Add at least one recipient');
      return;
    }
    setLoading(true);

    const results = await Promise.all(recipients.map(async (r) => {
      try {
        const payload = r.id ? { user_id: r.id, role } : { email: r.email, role };
        const response = await apiPost(`/projects/${projectId}/invitations`, payload);
        const data = await response.json().catch(() => ({}));
        return { recipient: r, ok: response.ok, error: data.error || data.errors?.[0] };
      } catch (err) {
        return { recipient: r, ok: false, error: err.message };
      }
    }));

    const sent = results.filter(r => r.ok);
    const failed = results.filter(r => !r.ok);

    if (sent.length > 0) {
      toast.success(`Sent ${sent.length} invitation${sent.length === 1 ? '' : 's'}`);
      onInviteSent?.();
    }
    failed.forEach(f => {
      const who = f.recipient.label || f.recipient.email || 'recipient';
      toast.error(`${who}: ${f.error || 'Failed'}`);
    });

    setLoading(false);

    if (failed.length === 0) {
      setRecipients([]);
      setQuery('');
      onClose();
    } else {
      setRecipients(prev => prev.filter(r =>
        failed.some(f => (f.recipient.id && f.recipient.id === r.id) || (f.recipient.email && f.recipient.email === r.email))
      ));
    }
  };

  const handleGenerateLink = async () => {
    setLoading(true);
    try {
      const response = await apiPost(`/projects/${projectId}/invitations`, { invite_type: 'link', role });
      if (response.ok) {
        const data = await response.json();
        setInviteUrl(data.invitation.invite_url);
        toast.success('Invite link generated');
      } else {
        const data = await response.json();
        toast.error(data.error || data.errors?.[0] || 'Failed to generate link');
      }
    } catch (error) {
      toast.error('Failed to generate link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const resetModal = () => {
    setMethod('email');
    setRecipients([]);
    setQuery('');
    setSuggestions([]);
    setRole('member');
    setInviteUrl('');
    setCopied(false);
    setShowDropdown(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Invite to Project" size="lg">
      {/* Method Tabs */}
      <div className={`flex gap-4 mb-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <button
          onClick={() => setMethod('email')}
          className={`pb-3 px-1 font-rasa transition-colors ${
            method === 'email'
              ? isDark ? 'border-b-2 border-blue-500 text-blue-400' : 'border-b-2 border-blue-500 text-blue-600'
              : isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Mail className="w-4 h-4 inline mr-2" />
          Email Invitation
        </button>
        <button
          onClick={() => setMethod('link')}
          className={`pb-3 px-1 font-rasa transition-colors ${
            method === 'link'
              ? isDark ? 'border-b-2 border-blue-500 text-blue-400' : 'border-b-2 border-blue-500 text-blue-600'
              : isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Copy className="w-4 h-4 inline mr-2" />
          Copy Link
        </button>
      </div>

      {/* Email Method */}
      {method === 'email' && (
        <div>
          <label className={`block mb-2 font-rasa text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Add people
          </label>

          <div className="relative">
            <div
              className={`flex flex-wrap items-center gap-1.5 px-2 py-1.5 rounded-lg border min-h-[42px] cursor-text ${
                isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
              }`}
              onClick={() => inputRef.current?.focus()}
            >
              {recipients.map(r => (
                <span
                  key={r.id || r.email}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-rasa ${
                    isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {r.label || r.email}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeRecipient(r.id || r.email); }}
                    className="hover:opacity-70"
                    aria-label="Remove"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                onKeyDown={handleKeyDown}
                placeholder={recipients.length === 0 ? 'Search by name or enter email' : ''}
                disabled={loading}
                className={`flex-1 min-w-[140px] bg-transparent outline-none text-sm font-rasa ${
                  isDark ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            {/* Suggestions dropdown */}
            {showDropdown && (query.trim() !== '' || suggestions.length > 0) && (
              <div
                ref={dropdownRef}
                className={`absolute z-10 w-full mt-1 max-h-56 overflow-y-auto rounded-lg border shadow-lg ${
                  isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                }`}
              >
                {suggestLoading ? (
                  <div className={`px-4 py-3 text-sm font-rasa text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Searching...
                  </div>
                ) : suggestions.length === 0 ? (
                  <div className={`px-4 py-3 text-sm font-rasa text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {EMAIL_RE.test(query.trim())
                      ? <>Press <span className="font-semibold">Enter</span> to invite <span className="font-semibold">{query.trim()}</span></>
                      : 'No matching users. Type a full email to invite.'}
                  </div>
                ) : (
                  suggestions.map((u, idx) => {
                    const label = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onMouseEnter={() => setHighlight(idx)}
                        onClick={() => addRecipient({
                          id: u.id, email: u.email, label, avatar_url: u.avatar_url
                        })}
                        className={`flex items-center gap-3 w-full px-3 py-2 text-left text-sm transition-colors ${
                          idx === highlight
                            ? isDark ? 'bg-gray-700' : 'bg-gray-100'
                            : ''
                        } ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      >
                        {u.avatar_url ? (
                          <img src={u.avatar_url} alt="" className="w-7 h-7 rounded-full" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs">
                            {(label || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-unna font-medium">{label}</p>
                          <p className={`text-xs font-rasa truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {u.email}
                          </p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <label className={`block mt-4 mb-2 font-rasa text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Role
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg font-rasa border transition-colors ${
              isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
            }`}
            disabled={loading}
          >
            <option value="viewer">Viewer (Read-only)</option>
            <option value="member">Member (Can edit)</option>
            <option value="editor">Editor (Can edit + invite)</option>
          </select>

          <p className={`mt-3 text-xs font-rasa ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Each recipient gets an email with an invitation link.
          </p>

          <button
            onClick={handleSendInvitations}
            disabled={loading || recipients.length === 0}
            className={`w-full mt-6 py-2 rounded-lg font-rasa font-semibold transition-colors ${
              loading || recipients.length === 0
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            {loading
              ? 'Sending...'
              : recipients.length === 0
                ? 'Send Invitation'
                : `Send ${recipients.length} Invitation${recipients.length === 1 ? '' : 's'}`}
          </button>
        </div>
      )}

      {/* Link Method (unchanged) */}
      {method === 'link' && (
        <div>
          <label className={`block mb-2 font-rasa text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Role
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg font-rasa border transition-colors ${
              isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
            }`}
            disabled={loading || inviteUrl}
          >
            <option value="viewer">Viewer</option>
            <option value="member">Member</option>
            <option value="editor">Editor</option>
          </select>

          {!inviteUrl ? (
            <>
              <p className={`mt-3 text-xs font-rasa ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Generate a link that anyone can use to join as a {role}
              </p>
              <button
                onClick={handleGenerateLink}
                disabled={loading}
                className={`w-full mt-6 py-2 rounded-lg font-rasa font-semibold transition-colors ${
                  loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                {loading ? 'Generating...' : 'Generate Invite Link'}
              </button>
            </>
          ) : (
            <>
              <div className={`mt-4 p-3 rounded-lg font-mono text-sm break-all ${
                isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
              }`}>
                {inviteUrl}
              </div>
              <button
                onClick={copyToClipboard}
                className="w-full mt-4 py-2 rounded-lg font-rasa font-semibold bg-green-500 hover:bg-green-600 text-white flex items-center justify-center gap-2 transition-colors"
              >
                {copied ? <><Check className="w-4 h-4" />Copied!</> : <><Copy className="w-4 h-4" />Copy Link</>}
              </button>
              <p className={`mt-3 text-xs font-rasa ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Anyone with this link can join as a <strong>{role}</strong>. Link expires in 7 days.
              </p>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default InviteMemberModal;
